import type { EChartsOption } from 'echarts'
import type { TooltipComponentFormatterCallbackParams } from 'echarts'
import type { ParsedTable, ChartGeneratorSettings } from '../types'
import {
  toNumber,
  axisPercentFormatter,
  tooltipPercentFormatter,
  resolveSeriesColors,
  GeneratorError,
  getChartColorByIndex,
  chartLegend,
  CHART_GRID_TOP_WITH_LEGEND,
  uniqueCategoryNames,
  cleanCategoryName,
} from './helpers';
import { orderRows } from '../utils/chartSort';

/**
 * Generates ECharts options for a vertical Column chart
 * Focuses on a single selected column (e.g., a specific time period)
 *
 * Similar to bar chart but with vertical orientation
 *
 * @param t - The parsed table data
 * @param title - The title for the chart
 * @param settings - Settings with `selectedHeader` for the data column
 * @returns ECharts option configuration object for a column chart
 * @throws {GeneratorError} If the table structure or settings are unsuitable
 */
export function columnOptions(
  t: ParsedTable,
  title: string,
  settings?: ChartGeneratorSettings
): EChartsOption {
  const selectedHeader = settings?.selectedHeader

  if (!selectedHeader) {
    throw new GeneratorError(
      'Column chart requires a `selectedHeader` in settings to choose the data column.'
    )
  }

  if (t.colCnt < 2 || t.rowCnt === 0) {
    throw new GeneratorError(
      'Column chart requires at least 2 columns (category + data) and 1 data row.'
    )
  }

  const headerIndex = t.headers.findIndex((h) => h === selectedHeader)

  if (headerIndex === -1) {
    const headerList = t.headers.join(', ')
    throw new GeneratorError(
      `Selected header "${selectedHeader}" not found in table headers: [${headerList}]`
    )
  }

  if (headerIndex === 0) {
    throw new GeneratorError(
      "Cannot use the first column header for Column chart data; it's assumed to be the category label."
    )
  }

  // Extract and sort data. Rows are picked by POSITION when the caller
  // sends positions (#1808) — a name matches every row carrying it — and by
  // name otherwise, as before.
  const sortOrder = settings?.sortOrder
  const rowPositions = settings?.selectedRowPositions
  const numericData = t.rows
    .filter((row, rowIndex) => {
      if (rowPositions && rowPositions.length > 0) return rowPositions.includes(rowIndex)
      if (settings?.selectedRows && settings.selectedRows.length > 0) {
        const categoryName = row[0] || 'Unnamed Category'
        return settings.selectedRows.includes(categoryName)
      }
      return true
    })
    .map((row) => {
      const categoryName = row[0] || 'Unnamed Category'
      const value = headerIndex < row.length ? toNumber(row[headerIndex]) : null
      return {
        name: categoryName,
        value: value,
      }
    })
    .filter((item) => item.value !== null && !isNaN(item.value))
  // No sort_order: the pre-#1808 order, untouched. With one: reading order
  // via the shared helper.
  const chartData = sortOrder
    ? orderRows(numericData, (item) => item.value, sortOrder)
    : numericData
      .sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity)) // Descending for column

  if (chartData.length === 0) {
    throw new GeneratorError(
      `No valid numeric data found for the selected header "${selectedHeader}".`
    )
  }

  const categoryNames = chartData.map((item) => item.name)
  // Repeated labels stay separate bars (#1808); unique ones are unchanged.
  const categories = uniqueCategoryNames(categoryNames)

  // Build data values with per-bar colors/gradients
  // Uses deterministic color generation for unknown codes to ensure consistency
  const dataValues = chartData.map((item, index) => {
    const key = item.name.toLowerCase();
    // Prefer gradient if available, then colorMap, then index-based fallback
    const gradient = settings?.gradientMap?.[key];
    const color = settings?.colorMap?.[key] || getChartColorByIndex(index);

    return {
      value: item.value,
      itemStyle: { color: gradient || color },
    }
  })

  return {
    title: {
      text: title,
      left: 'left',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      // `trigger: 'item'`, not 'axis' (#968 comment 4). An axis trigger always
      // hands the formatter an array, which the single-column formatter below
      // bails out of — so the tooltip rendered as an empty box. It also has
      // nothing to add here: one column sits at each axis position.
      trigger: 'item',
      confine: true,
      formatter: (params: TooltipComponentFormatterCallbackParams) => {
        if (Array.isArray(params)) {
          return ''
        }
        const p = params as any
        const value = typeof p.value === 'number' ? p.value : null
        const formattedValue =
          value !== null
            ? settings?.dataType === 'freq'
              ? value.toLocaleString()
              : `${(value * 100).toFixed(1)}%`
            : 'N/A'
        return `${cleanCategoryName(p.name)}<br/>${p.seriesName}: ${formattedValue}`
      },
    },
    // Default legend naming the plotted column (#1172) — see bar.ts for the
    // full rationale; the two single-column generators stay in step.
    legend: chartLegend([selectedHeader]),
    grid: {
      left: '3%',
      right: '4%',
      top: CHART_GRID_TOP_WITH_LEGEND,
      bottom: '15%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        // interval deliberately unset (#1178): ECharts' own 'auto' thins
        // colliding labels — 25 months no longer smear into one band. The
        // old force-every-label look is the "Every label" setting.
        rotate: 30,
        fontSize: 10,
        formatter: function (raw: string) {
          const value = cleanCategoryName(raw)
          return value.length > 15 ? value.substring(0, 15) + '...' : value
        },
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: settings?.dataType === 'freq' ? undefined : axisPercentFormatter,
      },
    },
    series: [
      {
        name: settings?.selectedHeader || 'Value',
        type: 'bar',
        data: dataValues,
        label: {
          show: true,
          position: 'top',
          formatter: (params) => {
            const value = typeof params.value === 'number' ? params.value : null
            return settings?.dataType === 'freq'
              ? value?.toLocaleString() || 'N/A'
              : tooltipPercentFormatter(value)
          },
          color: '#333',
          fontSize: 9,
        },
        emphasis: {
          focus: 'series',
        },
      },
    ],
    color: resolveSeriesColors(categoryNames, settings?.colorMap),
  }
}
